import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

type TriggerAction = { type: string; meta?: Record<string, unknown> };

const isPlainAction = (value: unknown): value is TriggerAction =>
  typeof value === 'object' && value !== null && typeof (value as TriggerAction).type === 'string';

/**
 * Makes an epic's follow-ups carry the meta of the action that triggered
 * them. Piped LAST onto the inner flow a trigger opens — after `catchError`,
 * so failure follow-ups inherit too:
 *
 *   mergeMap((trigger) =>
 *     from(write(trigger)).pipe(
 *       map(toSuccess),
 *       catchError(toError),
 *       inheritActionMeta(trigger),
 *     ),
 *   )
 *
 * The trigger is closed over, so inheritance stays exact under concurrency,
 * promises and timers — each inner flow stamps with its own trigger's meta,
 * whatever order they settle in. A trigger without meta makes this a
 * passthrough; an emission's own meta keys win over inherited ones; a
 * non-action emission (a deps creator) passes untouched.
 *
 * This is how a dispatched intent's provenance (`meta.source`) and outcome
 * correlation (`meta.dispatchId`) reach the follow-up the intent's caller
 * waits for. A chain of epics inherits hop by hop: every epic between the
 * intent and its declared outcome must apply this, or the meta stops there.
 */
export const inheritActionMeta =
  (trigger: TriggerAction) =>
  <T>(source$: Observable<T>): Observable<T> => {
    const inherited = trigger.meta;
    if (!inherited) return source$;
    return source$.pipe(
      map((emission) =>
        isPlainAction(emission)
          ? ({ ...emission, meta: { ...inherited, ...emission.meta } } as T)
          : emission,
      ),
    );
  };
